import * as Yup from 'yup';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useSnackbar } from 'notistack';
import { useTranslation } from '../../../common/LocalizationProvider';
import { createWithURLSearchParams } from '../../../fetch';
// ----------------------------------------------------------------------
export default function ResetPasswordForm() {
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslation();
  const location = useLocation();
  const forgot = location.pathname === '/reset/password';
  const token = location.search.slice(15);

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const ResetPasswordSchema = Yup.object({
    password: Yup.string().required(t('RPF_Password_Required')),
    confirmPassword: Yup.string()
      .required(t('RPF_Password_Required'))
      .oneOf([Yup.ref('password'), null], t('RPF_Valid_Password')),
    conditions: forgot
      ? null
      : Yup.boolean().isTrue(t('RPF_Condition_Required')).required(t('RPF_Condition_Required'))
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
      conditions: false
    },
    validationSchema: ResetPasswordSchema,
    onSubmit: async (values) => {
      const response = await createWithURLSearchParams(
        'password/update',
        `token=${token}&password=${values.password}&forgot=${forgot}`,
        'POST'
      );

      setSubmitting(false);

      if (response.ok) {
        enqueueSnackbar(t('RPF_Success'), {
          variant: 'success'
        });

        navigate('/login', { replace: true });
      } else {
        enqueueSnackbar(t('RPF_Error'), {
          variant: 'error'
        });
        navigate('/login', { replace: true });
      }
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setSubmitting } =
    formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };
  const handleShowPasswordConfirm = () => {
    setShowPasswordConfirm((show) => !show);
  };
  return (
    <FormikProvider value={formik}>
      <Stack spacing={3}>
        <TextField
          fullWidth
          autoComplete="current-password"
          type={showPassword ? 'text' : 'password'}
          label={t('RPF_Password')}
          {...getFieldProps('password')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleShowPassword} edge="end">
                  <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                </IconButton>
              </InputAdornment>
            )
          }}
          error={Boolean(touched.password && errors.password)}
          helperText={touched.password && errors.password}
        />
        <TextField
          fullWidth
          autoComplete="current-password"
          type={showPasswordConfirm ? 'text' : 'password'}
          label={t('RPF_ConfirmPassword')}
          {...getFieldProps('confirmPassword')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleShowPasswordConfirm} edge="end">
                  <Icon icon={showPasswordConfirm ? eyeFill : eyeOffFill} />
                </IconButton>
              </InputAdornment>
            )
          }}
          error={Boolean(touched.confirmPassword && errors.confirmPassword)}
          helperText={touched.confirmPassword && errors.confirmPassword}
        />
        {!forgot && (
          <Stack spacing={0}>
            <FormControlLabel
              control={<Checkbox {...getFieldProps('conditions')} checked={values.remember} />}
              label={
                <h5>
                  {t('RPF_Condition_Accept')}
                  <Link
                    variant="body2"
                    onClick={() =>
                      window.open('https://clickandtakeoff.com/condition-dutilisation/')
                    }
                    style={{ marginLeft: 5 }}
                  >
                    {t('RPF_Condition_Link')}
                  </Link>
                </h5>
              }
              name="conditions"
            />
            <FormHelperText error={Boolean(touched.conditions && errors.conditions)}>
              {touched.conditions && errors.conditions}
            </FormHelperText>
          </Stack>
        )}

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('RPF_Submit')}
        </LoadingButton>
      </Stack>
    </FormikProvider>
  );
}
